import { useMemo, useState, type ReactNode } from "react";
import type { FriendView } from "../api/client";
import { Avatar } from "./Avatar";
import { displayName } from "./profile-ui";
import { OfficialBadge, Sheet } from "./social-ui";
import { fa } from "../i18n/fa";

/**
 * «گفتگوی جدید» — pick a friend, start a conversation.
 *
 * ⚠️ THIS REPLACES A DEAD END. The compose ✎ used to call `setView("friends")`, and a friend row
 * in that tab opens the peer's PROFILE — which had no message action — so the button led nowhere
 * you could actually start a chat from. The only working path was typing a name into the chat
 * search box, which is precisely what a conversations list should not require.
 *
 * Same shape as the app's other pickers (`GameInviteSheet`, `SeatInviteSheet`): the shared
 * `Sheet`, so it inherits the slide-up + backdrop fade rather than inventing motion.
 *
 * PAGED, and deliberately so: the list is bounded by {@link PAGE} with a «بیشتر» step, and the
 * search box appears only past {@link SEARCH_THRESHOLD}. The worst real account today has 48
 * friends, so neither limit is reachable yet — it is the growth that is the risk, not the present.
 */
const PAGE = 12;
const SEARCH_THRESHOLD = 10;

export function FriendPickerSheet({
  friends,
  onPick,
  onClose,
}: {
  friends: readonly FriendView[];
  onPick: (friend: FriendView) => void;
  onClose: () => void;
}): ReactNode {
  const [query, setQuery] = useState("");
  const [shown, setShown] = useState(PAGE);

  const q = query.trim().toLowerCase();
  const matched = useMemo(
    () =>
      q
        ? friends.filter(
            (u) =>
              displayName(u).toLowerCase().includes(q) ||
              (u.appUsername ?? "").toLowerCase().includes(q),
          )
        : friends,
    [friends, q],
  );
  // Searching shows its own matches from the top; browsing pages down through the list.
  const visible = q ? matched.slice(0, PAGE) : matched.slice(0, shown);
  const remaining = matched.length - visible.length;

  return (
    <Sheet label={fa.chat.newConversation} onClose={onClose}>
      <h2 className="m-0 mb-1 font-body-lg text-body-lg font-bold text-on-surface">
        {fa.chat.newConversation}
      </h2>
      <p className="m-0 mb-3 font-caption text-caption text-on-surface-variant">
        {fa.chat.pickFriend}
      </p>

      {friends.length > SEARCH_THRESHOLD ? (
        <input
          type="text"
          aria-label={fa.chat.searchConversations}
          placeholder={fa.chat.searchConversations}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="mb-3 w-full appearance-none rounded-xl border border-surface-container-highest bg-surface-container-low px-4 py-2.5 font-body-md text-body-md text-on-surface outline-none transition-colors placeholder:text-outline-variant focus:border-secondary"
        />
      ) : null}

      {friends.length === 0 ? (
        <p className="m-0 py-4 text-center font-body-md text-body-md text-on-surface-variant">
          {fa.chat.noFriendsToChat}
        </p>
      ) : (
        <div className="flex max-h-[46vh] flex-col gap-1.5 overflow-y-auto overflow-x-hidden">
          {visible.map((u, i) => (
            <button
              key={u.userId}
              type="button"
              onClick={() => onPick(u)}
              // A short staggered rise, capped so a long list does not animate for a second —
              // the app's existing entrance idiom, not a new one.
              style={{ animationDelay: `${Math.min(i, 8) * 24}ms` }}
              className="game-grid-in flex w-full cursor-pointer appearance-none items-center gap-3 rounded-xl border border-outline-variant/50 bg-surface-container px-3 py-2.5 text-right transition-colors hover:bg-surface-container-high active:scale-[0.99]"
            >
              <Avatar
                avatarId={u.avatarId}
                avatarPhotoId={u.avatarPhotoId}
                size={40}
                ringClass="border-outline"
                ringWidth={1}
              />
              <span className="min-w-0 flex-1 truncate font-body-md text-body-md text-on-surface">
                {displayName(u)}
              </span>
              {u.isOfficial ? <OfficialBadge size={14} /> : null}
            </button>
          ))}
          {remaining > 0 ? (
            <button
              type="button"
              onClick={() => setShown((n) => n + PAGE)}
              className="mt-1 w-full cursor-pointer appearance-none rounded-xl border border-outline-variant/60 bg-transparent py-2 font-body-md text-body-md text-secondary active:scale-[0.99]"
            >
              {fa.chat.moreConversations}
            </button>
          ) : null}
        </div>
      )}
    </Sheet>
  );
}
